import React from 'react';
import type { NextPageWithLayout, GetServerSideProps } from 'next';
import { Entry, Op, OrderBy } from 'contensis-delivery-api';

import contensis from 'Common/api/contensis';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata } from 'Common/util/contensis/api';
import { parseFilterQuery } from 'Common/util/string';
import { type Project } from 'Common/hooks/useFilters';

import MainLayout from 'Layout/Main';
import Metadata from 'Components/Metadata';
import Template, { datamap, type PageData } from 'Templates/Search';

const searchResultFieldList: string[] = [
    'sys.slug',
    'image',
    'heroImage',
    'courseCardImage',
    'pageDataEntry',
    'heroBanner',
    'videoThumbnail',
    'title',
    'description',
    'pageMetadata',
    'description',
    'asset',
    'customTags',
    'isIndependentBlog',
    'id',
    'altText',
    'alt',
    'ariaLabel',
    'src',
    'href',
    ...excludeMetadata
];

const Search: NextPageWithLayout<PageData> = ({ entry, ...rest }) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template {...datamap({ entry, ...rest })} />
        </>
    );
};

Search.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ preview, res, query }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });
    const { fetch: fetchGlobal } = contensis({ projectId: 'global' });

    const searchTerm: string = parseFilterQuery(query.search).join(' ');
    const filters: string[] = parseFilterQuery(query.filter);
    const project: Project = parseFilterQuery(query.project || 'website')[0] as Project;

    const { fetch: fetchSearchResults } = contensis({
        projectId: project
    });

    const { data, error } = await fetch(async client => {
        const resPage = await client.nodes.get({
            path: '/search',
            entryLinkDepth: 1,
            entryFields: ['*', ...excludeMetadata]
        });

        if (!resPage.entry) {
            return { error: true };
        }

        const entry = resPage.entry;

        return { entry };
    });

    const { data: searchConfig, error: errorGlobal } = await fetchGlobal(
        async (_client, search) => {
            const resConfig = await search(Op.equalTo('sys.contentTypeId', 'searchConfiguration'), {
                fields: [
                    'site',
                    'label',
                    'apiName',
                    'filters',
                    'contentTypeIDs',
                    'pageSize',
                    'fieldWeights',
                    ...excludeMetadata
                ],
                linkDepth: 1
            });

            if (resConfig.items.length < 1) {
                return { error: true };
            }

            return { projects: resConfig.items };
        }
    );

    const [searchResultsQuery, currentTabSiteConfig] = createSearchResultsQuery(
        searchConfig,
        filters,
        project,
        searchTerm
    );

    const { data: searchResults, error: errorSearchResults } = await fetchSearchResults(
        async (_client, search) => {
            const resFeaturedSearchResults = await search(
                Op.equalTo('sys.contentTypeId', 'search'),
                {
                    fields: [
                        'featuredSearchItem',
                        'queryKeywords',
                        'results',
                        ...searchResultFieldList
                    ],
                    linkDepth: 1
                }
            );

            const [featuredEntriesIds, featuredEntries] = extractFeaturesEntries(
                resFeaturedSearchResults,
                searchTerm
            );

            const resSearchResults = await search(
                Op.and(searchResultsQuery, Op.not(Op.in('sys.id', ...featuredEntriesIds))),
                {
                    pageSize: currentTabSiteConfig.pageSize,
                    pageIndex: parseInt(query.page as string) - 1 || 0,
                    linkDepth: 3,
                    orderBy: !searchTerm ? OrderBy.asc('title') : undefined,
                    fields: searchResultFieldList
                }
            );

            return {
                results: resSearchResults,
                featuredEntries
            };
        }
    );

    if (error || errorGlobal || errorSearchResults) {
        return {
            notFound: true
        };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return {
            props: {
                ...data,
                searchConfig: searchConfig?.projects || [],
                searchResults: searchResults?.results,
                featuredSearchResults: searchResults?.featuredEntries,
                activeSearchTerm: searchTerm,
                activeFilterQueries: filters,
                selectedProject: project
            }
        };
    }
};

const createSearchResultsQuery = (searchConfig, filters, project, searchTerm) => {
    const currentTabSiteConfig = searchConfig.projects.find(item => item.site.apiName === project);

    if (!searchConfig || !searchConfig.projects || !currentTabSiteConfig) {
        return [];
    }

    const currentTabContentTypeFilters = currentTabSiteConfig.filters.filter(item =>
        filters.includes(item.typeLabel)
    );

    const currentTabContentTypes =
        currentTabContentTypeFilters.length > 0
            ? currentTabContentTypeFilters.map(filter => filter.key.split(',')).flat()
            : currentTabSiteConfig.contentTypeIDs.map(item => item.value);

    const currentTabFieldWeights = currentTabSiteConfig.fieldWeights;

    const currentTabContentTypesQuery = Op.in('sys.contentTypeId', ...currentTabContentTypes);

    const currentTabFieldWeightsQuery = searchTerm
        ? currentTabFieldWeights.map(fieldWeight =>
              Op.freeText(fieldWeight.field, searchTerm).weight(fieldWeight.customWeight)
          )
        : [];

    return [
        Op.and(
            currentTabContentTypesQuery,
            Op.or(...currentTabFieldWeightsQuery),
            Op.or(
                Op.exists('sys.uri', true),
                Op.in('sys.contentTypeId', 'newsArticle', 'blogArticle', 'event', 'videoArticle')
            )
        ),
        currentTabSiteConfig
    ];
};

const extractFeaturesEntries = (resFeaturedSearchResults, searchTerm): [string[], Entry[]] => {
    const featuredEntriesIds: string[] = [];
    const featuredEntries: Entry[] = [];

    if (resFeaturedSearchResults.items.length > 0) {
        const searchEntry = resFeaturedSearchResults.items[0];
        const featuredItems = searchEntry.featuredSearchItem;

        featuredItems.map(item => {
            if (item.queryKeywords.toLowerCase().split(', ').includes(searchTerm.toLowerCase())) {
                featuredEntriesIds.push(...item.results.map(entry => entry.sys.id));
                featuredEntries.push(...item.results);
            }
        });
    }

    return [featuredEntriesIds, featuredEntries];
};

export default Search;
